/**
 * 병의원 상세·수정 화면.
 *
 * admin-ruda-web `hospitalInfoDetails.js` 포팅.
 * - 사업자등록증: 업로드/OCR/미리보기 없음. 없으면 미등록, 있으면 다운로드·보기만
 * - 상단 행: 병원 고유번호·병의원 구분·요양기관번호(읽기전용) + 병의원명(필수·수정)
 */
import {
  useHospitalDetail,
  useUpdateHospital,
} from '@/entities/hospital/hooks/useHospitals';
import { useMenuAccess } from '@/layouts/MenuAccessContext';
// import { MedicalDepartmentsEditModal } from '@/pages/institutions/MedicalDepartmentsEditModal';
import { AddressSearchField } from '@/shared/components/AddressSearchField';
import { FormTextField } from '@/shared/components/FormTextField';
import { LoadingSpinner } from '@/shared/components/LoadingSpinner';
import { MenuWriteGate } from '@/shared/components/MenuWriteGate';
import { PageHeader } from '@/shared/components/PageHeader';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/shared/components/ui/alert-dialog';
import { Button } from '@/shared/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/components/ui/card';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/shared/components/ui/form';
import { Input } from '@/shared/components/ui/input';
import { cn } from '@/shared/lib/utils';
import { ROUTE_PATHS } from '@/shared/constants/routes';
import { Badge } from '@/shared/components/ui/badge';
import { formatClosedOnDisplay } from '@/shared/utils/facilityClosure';
import {
  formatYkihoDisplay,
  getPartnerBusinessStatusBadgeVariant,
  getPartnerBusinessStatusLabel,
} from '@/shared/utils/partnerBusinessStatus';
import { handleApiError, useToast } from '@/shared/utils/toast';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate, useParams } from '@tanstack/react-router';
import { Building2, Download, Eye } from 'lucide-react';
// Pencil — 진료과 편집 버튼 복구 시 함께 복원
import React, { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { FooterRow } from '@/shared/components/ui/row';

const institutionDetailSchema = z.object({
  clinicUniqueCode: z.string(),
  ykiho: z.string(),
  clinicTypeCode: z.string(),
  clinicName: z.string().min(1, '병의원명을 입력해주세요'),
  clinicAddress: z.string(),
  clinicPhoneNumber: z.string(),
  businessLicenseNumber: z.string(),
  representativeName: z.string(),
  managerRole: z.string(),
  managerName: z.string(),
  managerPhoneNumber: z.string(),
  managerEmail: z.string(),
  medicalDepartments: z.string(),
});

type InstitutionDetailFormValues = z.infer<typeof institutionDetailSchema>;

/** 레거시 DownloadWithPreview와 동일 — blob으로 받아 파일명 지정 다운로드 */
const downloadLicenseFile = async (uri: string, downloadName: string) => {
  const url = new URL(uri);
  const response = await fetch(`${url.origin}${url.pathname}`);
  if (!response.ok) {
    throw new Error(`다운로드 실패 (${response.status})`);
  }
  const blob = await response.blob();
  const blobUrl = URL.createObjectURL(blob);
  const extension = url.pathname.split('.').pop() || 'png';
  const link = document.createElement('a');
  link.href = blobUrl;
  link.download = `${downloadName}.${extension}`;
  link.style.display = 'none';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(blobUrl);
};

export const InstitutionDetailPage: React.FC = () => {
  const toast = useToast();
  const navigate = useNavigate();
  const { readOnly } = useMenuAccess();
  const { clinicId: clinicIdParam } = useParams({ strict: false }) as {
    clinicId?: string;
  };
  const clinicId = Number(clinicIdParam);

  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [quitDialogOpen, setQuitDialogOpen] = useState(false);
  // const [deptModalOpen, setDeptModalOpen] = useState(false);
  const [licenseDownloading, setLicenseDownloading] = useState(false);

  const { data, isLoading, isError, error } = useHospitalDetail(
    Number.isFinite(clinicId) ? clinicId : null,
  );
  const updateMutation = useUpdateHospital();

  const form = useForm<InstitutionDetailFormValues>({
    resolver: zodResolver(institutionDetailSchema),
    defaultValues: {
      clinicUniqueCode: '',
      ykiho: '',
      clinicTypeCode: '',
      clinicName: '',
      clinicAddress: '',
      clinicPhoneNumber: '',
      businessLicenseNumber: '',
      representativeName: '',
      managerRole: '',
      managerName: '',
      managerPhoneNumber: '',
      managerEmail: '',
      medicalDepartments: '',
    },
  });

  /** 진료과 모달 적용 직후 React Query refetch가 form.reset으로 덮어쓰지 않도록 */
  const loadedClinicIdRef = useRef<number | null>(null);

  useEffect(() => {
    if (!data) return;

    const isNewClinic = loadedClinicIdRef.current !== clinicId;
    // 같은 병의원을 편집 중이면 서버 refetch로 로컬 수정(진료과 등)을 지우지 않는다
    if (!isNewClinic && form.formState.isDirty) return;

    form.reset({
      clinicUniqueCode: data.clinicUniqueCode ?? '',
      ykiho:
        formatYkihoDisplay(data.ykiho) === '-'
          ? ''
          : (data.ykiho ?? ''),
      clinicTypeCode: data.clinicTypeCode ?? '',
      clinicName: data.clinicName ?? '',
      clinicAddress: data.clinicAddress ?? '',
      clinicPhoneNumber: data.clinicPhoneNumber ?? '',
      businessLicenseNumber: data.businessLicenseNumber ?? '',
      representativeName: data.representativeName ?? '',
      managerRole: data.managerRole ?? '',
      managerName: data.managerName ?? '',
      managerPhoneNumber: data.managerPhoneNumber ?? '',
      managerEmail: data.managerEmail ?? '',
      medicalDepartments: data.medicalDepartments?.join(', ') ?? '',
    });
    loadedClinicIdRef.current = clinicId;
  }, [clinicId, data, form]);

  const medicalDepartmentsText = form.watch('medicalDepartments');
  // const medicalDepartmentsList = medicalDepartmentsText
  //   .split(',')
  //   .map((dept) => dept.trim())
  //   .filter(Boolean);

  useEffect(() => {
    if (isError && error) {
      handleApiError(error, toast);
    }
  }, [isError, error, toast]);

  const licenseImageUrl = data?.businessLicenseImage?.trim() || '';
  const hasLicense = Boolean(licenseImageUrl);

  const handleBack = () => {
    if (form.formState.isDirty) {
      setQuitDialogOpen(true);
      return;
    }
    navigate({ to: ROUTE_PATHS.INSTITUTIONS } as never);
  };

  const handleSave = async (values: InstitutionDetailFormValues) => {
    if (!Number.isFinite(clinicId)) return;

    try {
      await updateMutation.mutateAsync({
        clinicId,
        data: {
          clinicName: values.clinicName,
          clinicPhoneNumber: values.clinicPhoneNumber || undefined,
          clinicAddress: values.clinicAddress || undefined,
          businessLicenseNumber: values.businessLicenseNumber || undefined,
          representativeName: values.representativeName || undefined,
          managerRole: values.managerRole || undefined,
          managerName: values.managerName || undefined,
          managerPhoneNumber: values.managerPhoneNumber || undefined,
          managerEmail: values.managerEmail || undefined,
          medicalDepartments: values.medicalDepartments
            .split(',')
            .map((dept) => dept.trim())
            .filter(Boolean),
        },
      });
      toast.success('병원 정보가 성공적으로 저장되었습니다.');
      form.reset(values);
      setSaveDialogOpen(false);
    } catch (err) {
      handleApiError(err, toast);
    }
  };

  const handleLicenseDownload = async () => {
    if (!licenseImageUrl) return;
    setLicenseDownloading(true);
    try {
      await downloadLicenseFile(
        licenseImageUrl,
        `${data?.clinicName || '병의원'}_사업자 등록증`,
      );
    } catch (err) {
      handleApiError(err, toast);
    } finally {
      setLicenseDownloading(false);
    }
  };

  const handleLicenseView = () => {
    if (!licenseImageUrl) return;
    window.open(licenseImageUrl, '_blank', 'width=1000,height=1000');
  };

  if (!Number.isFinite(clinicId)) {
    return (
      <div className="py-16 text-center text-muted-foreground">
        잘못된 병의원 ID입니다.
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="py-16">
        <LoadingSpinner tip="병의원 정보를 불러오는 중..." />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="py-16 text-center text-muted-foreground">
        병의원 정보를 찾을 수 없습니다.
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        icon={Building2}
        title="병의원 상세"
        description={data.clinicName}
        actions={
          <FooterRow gap={8}>
            <MenuWriteGate>
              <Button
                type="button"
                onClick={form.handleSubmit(() => setSaveDialogOpen(true))}
                disabled={!form.formState.isDirty || updateMutation.isPending}
                className="ruda-button"
              >
                저장
              </Button>
            </MenuWriteGate>
            <Button type="button" variant="outline" onClick={handleBack}>
              나가기
            </Button>
          </FooterRow>
        }
      />

      <Card>
        <CardHeader>
          <CardTitle>{data.clinicName}</CardTitle>
        </CardHeader>
        <CardContent>
          <Form {...form}>
            <form
              className="space-y-6"
              onSubmit={form.handleSubmit(() => setSaveDialogOpen(true))}
            >
              {/* 레거시와 동일: 고유번호·구분·요양기관번호(읽기) + 병의원명(필수·수정) */}
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
                <FormField
                  control={form.control}
                  name="clinicUniqueCode"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>병원 고유번호</FormLabel>
                      <FormControl>
                        <Input {...field} disabled />
                      </FormControl>
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="clinicTypeCode"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>병의원 구분</FormLabel>
                      <FormControl>
                        <Input {...field} disabled />
                      </FormControl>
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="ykiho"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>요양기관번호</FormLabel>
                      <FormControl>
                        <Input {...field} disabled />
                      </FormControl>
                    </FormItem>
                  )}
                />
                <FormTextField
                  control={form.control}
                  name="clinicName"
                  label="병의원명"
                  required
                  placeholder="병의원명"
                  disabled={readOnly}
                />
              </div>

              <div className="grid gap-4 md:grid-cols-3">
                <div className="space-y-2">
                  <FormLabel>사업자 등록증</FormLabel>
                  {hasLicense ? (
                    <div className="flex flex-wrap gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        disabled={licenseDownloading}
                        onClick={() => void handleLicenseDownload()}
                      >
                        <Download className="mr-2 h-4 w-4" />
                        다운로드
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={handleLicenseView}
                      >
                        <Eye className="mr-2 h-4 w-4" />
                        보기
                      </Button>
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground">미등록</p>
                  )}
                </div>
                <FormField
                  control={form.control}
                  name="businessLicenseNumber"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>사업자등록번호</FormLabel>
                      <FormControl>
                        <Input {...field} disabled={readOnly} />
                      </FormControl>
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="representativeName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>대표자명</FormLabel>
                      <FormControl>
                        <Input {...field} disabled={readOnly} />
                      </FormControl>
                    </FormItem>
                  )}
                />
              </div>

              <div className="grid gap-4 md:grid-cols-3">
                <FormField
                  control={form.control}
                  name="clinicPhoneNumber"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>병의원 전화번호</FormLabel>
                      <FormControl>
                        <Input {...field} disabled={readOnly} />
                      </FormControl>
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="clinicAddress"
                  render={({ field }) => (
                    <FormItem className="md:col-span-2">
                      <FormLabel>주소</FormLabel>
                      <AddressSearchField
                        value={field.value}
                        onChange={field.onChange}
                        onBlur={field.onBlur}
                        name={field.name}
                        disabled={readOnly}
                      />
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
                <FormField
                  control={form.control}
                  name="managerRole"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>담당자 직책</FormLabel>
                      <FormControl>
                        <Input {...field} disabled={readOnly} />
                      </FormControl>
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="managerName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>담당자명</FormLabel>
                      <FormControl>
                        <Input {...field} disabled={readOnly} />
                      </FormControl>
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="managerPhoneNumber"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>담당자 연락처</FormLabel>
                      <FormControl>
                        <Input {...field} disabled={readOnly} />
                      </FormControl>
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="managerEmail"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>담당자 이메일</FormLabel>
                      <FormControl>
                        <Input {...field} type="email" disabled={readOnly} />
                      </FormControl>
                    </FormItem>
                  )}
                />
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <FormField
                  control={form.control}
                  name="medicalDepartments"
                  render={({ field }) => (
                    <FormItem>
                      <div className="flex items-center justify-between gap-2">
                        <FormLabel>진료과</FormLabel>
                        {/* 진료과 편집 — 일시 비활성화
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          onClick={() => setDeptModalOpen(true)}
                        >
                          <Pencil className="mr-1 h-3 w-3" />
                          편집
                        </Button>
                        */}
                      </div>
                      <FormControl>
                        <textarea
                          {...field}
                          value={medicalDepartmentsText}
                          rows={4}
                          readOnly
                          className={cn(
                            'flex min-h-[80px] w-full rounded-md border border-input bg-muted px-3 py-2 text-sm',
                          )}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
                <div className="space-y-2">
                  {/* businessStatusType: closedOn·공공인허가 휴업 매칭 판정값 */}
                  <p className="text-sm font-medium leading-none">영업 상태</p>
                  <div className="flex h-10 items-center">
                    <Badge
                      variant={getPartnerBusinessStatusBadgeVariant(
                        data.businessStatusType,
                      )}
                    >
                      {getPartnerBusinessStatusLabel(data.businessStatusType)}
                    </Badge>
                  </div>
                </div>
                <div className="space-y-2">
                  <p className="text-sm font-medium leading-none">폐업일</p>
                  <p className="rounded-md border bg-muted px-3 py-2 text-sm">
                    {formatClosedOnDisplay(data.closedOn)}
                  </p>
                </div>
              </div>
            </form>
          </Form>
        </CardContent>
      </Card>

      <AlertDialog open={saveDialogOpen} onOpenChange={setSaveDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>저장 확인</AlertDialogTitle>
            <AlertDialogDescription>
              변경된 내용으로 저장됩니다.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>취소</AlertDialogCancel>
            <AlertDialogAction
              onClick={form.handleSubmit(handleSave)}
              disabled={updateMutation.isPending}
            >
              확인
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={quitDialogOpen} onOpenChange={setQuitDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>나가기</AlertDialogTitle>
            <AlertDialogDescription>
              저장하지 않은 변경 사항이 있습니다. 목록으로 돌아가시겠습니까?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>계속 편집</AlertDialogCancel>
            <AlertDialogAction
              onClick={() =>
                navigate({ to: ROUTE_PATHS.INSTITUTIONS } as never)
              }
            >
              나가기
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* 진료과 편집 모달 — 편집 버튼과 함께 일시 비활성화
      <MedicalDepartmentsEditModal
        open={deptModalOpen}
        departments={medicalDepartmentsList}
        onClose={() => setDeptModalOpen(false)}
        onSave={(departments) => {
          form.setValue('medicalDepartments', departments.join(', '), {
            shouldDirty: true,
            shouldTouch: true,
            shouldValidate: true,
          });
        }}
      />
      */}
    </div>
  );
};
